import { Grid } from '@mui/material';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import HistorianFamilyStepButtons from 'cohabitation/components/historian-family-step-buttons';
import HistorianFamilyContent from 'cohabitation/components/content/historian-data-view';
import { Fragment, useEffect } from 'react';
import { noop } from 'shell/utils/noop';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { Form, Formik } from 'formik';
import { HistoryCohabitationVM } from 'cohabitation/view-models/history-cohabitation';
import { HolderResponse, ResponseFamilyData } from 'cohabitation/pages/historian';
import { DateTime } from 'luxon';
import { HistoryCohabitationCrumbs } from 'cohabitation/constants/breadcrumb';

const HistorianFamilyPage = (): React.ReactElement => {
  const [queryParams] = useSearchParams();

  const navigate = useNavigate();

  const familyId = queryParams.get('idFamigliaConvivenza') || '';
  const familyIdAnpr = queryParams.get('idFamigliaAnpr') || '';

  const [family, getFamily] = useHttpFetch<HttpResponse<ResponseFamilyData>, ResponseFamilyData>(
    `${__PERSON_API_URL__}/persona/convivenza/${familyId}`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response) => {
        return response.data.payload;
      },
    }
  );

  const [holder, getHolder] = useHttpFetch<HttpResponse<HolderResponse>, HolderResponse>(
    `${__PERSON_API_URL__}/persona/capo-convivenza/ultimo/${familyId}`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response) => {
        return response.data.payload;
      },
    }
  );

  useEffect(() => {
    if (familyId) {
      getHolder();
      getFamily();
    }
  }, [getFamily, getHolder]);

  if (family.pristine || family.isLoading || holder.pristine || holder.isLoading) {
    return <></>;
  }

  const castedFamily = family.data as ResponseFamilyData;
  const castedHolder = holder.data as HolderResponse;

  const form: HistoryCohabitationVM = {
    cohabitationData: {
      idAnpr: familyIdAnpr,
      idSipo: familyId,
      type: castedFamily?.tipo || '',
      creationDate: castedFamily?.dataIstituzione
        ? DateTime.fromISO(castedFamily?.dataIstituzione)
        : null,
      description: castedFamily?.nome || '',
    },
    historyDataManagement: {
      name: castedFamily?.nome || '',
      type: castedFamily?.tipo || '',
      institutionDate: castedFamily?.dataIstituzione
        ? DateTime.fromISO(castedFamily?.dataIstituzione)
        : null,
      closeDate: castedFamily?.dataCessazione
        ? DateTime.fromISO(castedFamily?.dataCessazione)
        : null,
      phone1: castedFamily?.telefono1 || '',
      phone2: castedFamily?.telefono2 || '',
      phone3: castedFamily?.telefono3 || '',
      fiscalCode: castedFamily?.codiceFiscale || '',
      lastHeadCohabitation: castedHolder?.nome || '',
      birthHeadCohabitation: castedHolder?.dataNascita
        ? DateTime.fromISO(castedHolder?.dataNascita)
        : null,
      placeHeadCohabitation: castedHolder?.descrizioneComuneNascita || '',
      genderHeadCohabitation: castedHolder?.sesso || '',
    },
    address: castedFamily?.descrizioneToponimo || '',
    letterCivic: castedFamily?.esponenteCivico?.toString() || '',
    numberCivic: castedFamily?.numeroCivico?.toString() || '',
  };

  return (
    <Fragment>
      <Breadcrumbs items={HistoryCohabitationCrumbs} />
      <div className="pmi-mt-4">
        <div className="pmi-mt-2">
          <Formik initialValues={form} onSubmit={noop}>
            <Form>
              <Grid container spacing={1}>
                <Grid item xs={12} md={2}>
                  <HistorianFamilyStepButtons onCancel={() => navigate('/convivenza/ricerca')} />
                </Grid>
                <Grid item xs={12} md={10}>
                  <div className="pmi-px-md-4">
                    <HistorianFamilyContent isEdit />
                  </div>
                </Grid>
              </Grid>
            </Form>
          </Formik>
        </div>
      </div>
    </Fragment>
  );
};

HistorianFamilyPage.displayName = 'Pagina storico famiglia';

export default HistorianFamilyPage;
